import { customersColumns, getWIPColumns, handoverColumns, valCallsColumns } from "@/constants/pipeline";
import { CustomerModel, Handover, ValCallModel, WIPModel } from "@/models/pipeline";
import { mockWorkers } from "@/services/mockData";
import { useMemo, useState } from "react";
import { Accordion } from "./Accordion";
import { CustomerRow } from "./CustomerRow";
import { HandoverRow } from "./HandoverRow";
import { TopLevelStatsBox } from "./TopLevelStatsBox";
import { ValCallRow } from "./ValCallRow";
import { WIPRow } from "./WIPRow";

interface PipelineStatistics {
  appsTotal?: number;
  warmAppsTotal?: number;
  allocatedOnSelectedDayTotal?: number;
  leadsToday?: number;
  leadsTotal?: number;
  predictedToDocs: number;
  docsToday: number;
  gapDocsOut: number;
  gapReadyToGo: number;
  gapPayout: number;
  gapTotal: number;
  predictedToPay: number;
  paidToday: number;
}

interface BreakdownViewProps {
  selectedDepartmentId: string | null;
  selectedAgentId: number;
  pipelineType: "agent" | "department" | "all";
  statistics: PipelineStatistics | null;
  selectedDate: Date;
  handovers: Handover[];
  valCalls: ValCallModel[];
  wip: WIPModel[];
  newLeads: CustomerModel[];
  pinnedWarmCustomers: CustomerModel[];
  warm: CustomerModel[];
  customers: CustomerModel[];
  currentAgentId: number;
  pinnedApplications: string[];
  setPinnedApplications: (applications: string[]) => void;
  setSelectedAgentId: (agentId: number) => void;
}

export const BreakdownView = ({
  selectedDepartmentId,
  selectedAgentId,
  pipelineType,
  statistics,
  selectedDate,
  handovers,
  valCalls,
  wip,
  newLeads,
  pinnedWarmCustomers,
  warm,
  customers,
  currentAgentId,
  pinnedApplications,
  setPinnedApplications,
  setSelectedAgentId,
}: BreakdownViewProps) => {
  const [expandedPanels, setExpandedPanels] = useState<Record<string, boolean>>({});
  const [sortState, setSortState] = useState<Record<string, { sortBy: string; sortOrder: "asc" | "desc" } | null>>({});

  const isPinned = (id: string) => {
    return pinnedApplications.includes(id);
  };

  const handlePanelChange = (panelId: string) => (isExpanded: boolean) => {
    setExpandedPanels((prev) => ({ ...prev, [panelId]: isExpanded }));
  };

  const handleSort = (accordionId: string, sortKey: string) => {
    setSortState((prev) => {
      const currentSort = prev[accordionId];

      if (!currentSort || currentSort.sortBy !== sortKey) {
        return {
          ...prev,
          [accordionId]: { sortBy: sortKey, sortOrder: "asc" },
        };
      } else if (currentSort.sortOrder === "asc") {
        return {
          ...prev,
          [accordionId]: { sortBy: sortKey, sortOrder: "desc" },
        };
      } else {
        return {
          ...prev,
          [accordionId]: null,
        };
      }
    });
  };

  const handlePin = async (applicationId: string) => {
    if (!currentAgentId) return;

    const newPinnedList = [...pinnedApplications];
    const index = newPinnedList.indexOf(applicationId);

    if (index > -1) {
      newPinnedList.splice(index, 1);
    } else {
      newPinnedList.push(applicationId);
    }

    setPinnedApplications(newPinnedList);
  };

  const agents = useMemo(() => {
    return mockWorkers.map((worker) => ({
      id: worker.userId,
      name: worker.name,
      departmentId: worker.departmentId,
      departmentName: worker.department,
      isTeamLeader: false,
    }));
  }, []);

  const handleAgentNameClick = (agentName: string) => {
    if (!agentName || !agents) return;

    const agent = agents.find((a) => a.name === agentName);
    if (agent) {
      setSelectedAgentId(agent.id);
    }
  };

  const isDepartmentView = selectedAgentId === -1 && selectedDepartmentId !== "all";

  const wipColumns = isDepartmentView ? getWIPColumns("department") : getWIPColumns("agent");

  const isAgentView = pipelineType === "agent";

  return (
    <div className="flex flex-grow overflow-hidden">
      <div className="h-full flex-grow overflow-x-hidden overflow-y-auto">
        <TopLevelStatsBox statistics={statistics} selectedDate={selectedDate} />

        <div className="flex flex-col gap-3 p-3 md:p-3">
          {selectedDepartmentId !== "all" && selectedAgentId === -1 && (
            <Accordion
              title="Handovers"
              id="handovers"
              count={handovers.length}
              columns={handoverColumns}
              stats={{
                handoversToday: handovers.length,
              }}
              expanded={expandedPanels["handovers"] ?? handovers.length > 0}
              onChange={handlePanelChange("handovers")}
              classes={{
                wrapper: "!bg-sky-50 !border-sky-700/10",
                counter: "!bg-sky-100 !border-sky-700/30",
              }}
              sortBy={sortState["handovers"]?.sortBy}
              sortOrder={sortState["handovers"]?.sortOrder}
              onSort={(sortKey) => handleSort("handovers", sortKey)}
            >
              {handovers.map((item: Handover) => (
                <HandoverRow
                  key={item.applicationId}
                  data={item}
                  columns={handoverColumns}
                  isPinned={isPinned(item.applicationId?.toString())}
                  isDepartmentView={isDepartmentView}
                  onTogglePin={handlePin}
                />
              ))}
            </Accordion>
          )}

          {selectedDepartmentId !== "all" && (
            <>
              <Accordion
                title="Val Calls"
                id="val-calls"
                count={valCalls.length}
                columns={valCallsColumns}
                expanded={expandedPanels["val-calls"] ?? valCalls.length > 0}
                onChange={handlePanelChange("val-calls")}
                sortBy={sortState["val-calls"]?.sortBy}
                sortOrder={sortState["val-calls"]?.sortOrder}
                onSort={(sortKey) => handleSort("val-calls", sortKey)}
              >
                {valCalls.map((item: ValCallModel) => (
                  <ValCallRow
                    key={item.applicationId}
                    data={item}
                    agentId={selectedAgentId ?? -1}
                    columns={valCallsColumns}
                    isPinned={isPinned(item.applicationId.toString())}
                    onTogglePin={handlePin}
                  />
                ))}
              </Accordion>

              <Accordion
                title="WIP"
                id="wip"
                count={wip.length}
                columns={wipColumns}
                expanded={expandedPanels["wip"] ?? wip.length > 0}
                onChange={handlePanelChange("wip")}
                sortBy={sortState["wip"]?.sortBy}
                sortOrder={sortState["wip"]?.sortOrder}
                onSort={(sortKey) => handleSort("wip", sortKey)}
              >
                {wip.map((item: WIPModel, index: number) => (
                  <WIPRow
                    key={`wip-${item.applicationId}-${index}`}
                    data={item}
                    agentId={selectedAgentId ?? -1}
                    columns={wipColumns}
                    isPinned={isPinned(item.applicationId?.toString())}
                    onTogglePin={handlePin}
                  />
                ))}
              </Accordion>

              {isAgentView && <div className="my-2 h-px w-full bg-slate-200" />}

              {isAgentView && newLeads.length > 0 && (
                <Accordion
                  title="New"
                  id="newLeads"
                  count={newLeads.length}
                  columns={customersColumns}
                  classes={{
                    wrapper: "!bg-[#FFEDD5] !border-[#FED7AA]",
                    counter: "!bg-orange-200 !border-orange-300",
                  }}
                  expanded={expandedPanels["newLeads"] ?? newLeads.length > 0}
                  onChange={handlePanelChange("newLeads")}
                  sortBy={sortState["newLeads"]?.sortBy}
                  sortOrder={sortState["newLeads"]?.sortOrder}
                  onSort={(sortKey) => handleSort("newLeads", sortKey)}
                >
                  {newLeads.map((item: CustomerModel) => (
                    <CustomerRow
                      key={item.applicationId}
                      data={item}
                      agentId={selectedAgentId ?? -1}
                      columns={customersColumns}
                      isPinned={isPinned(item.applicationId.toString())}
                      isDepartmentView={isDepartmentView}
                      onAgentNameClick={handleAgentNameClick}
                      onTogglePin={handlePin}
                    />
                  ))}
                </Accordion>
              )}

              {isAgentView && pinnedWarmCustomers.length > 0 && (
                <Accordion
                  title="Pinned"
                  id="pinned"
                  count={pinnedWarmCustomers.length}
                  columns={customersColumns}
                  classes={{
                    wrapper: "!bg-[#FFEDD5] !border-[#FED7AA]",
                    counter: "!bg-orange-200 !border-orange-300",
                  }}
                  expanded={expandedPanels["pinned"] ?? pinnedWarmCustomers.length > 0}
                  onChange={handlePanelChange("pinned")}
                  sortBy={sortState["pinned"]?.sortBy}
                  sortOrder={sortState["pinned"]?.sortOrder}
                  onSort={(sortKey) => handleSort("pinned", sortKey)}
                >
                  {pinnedWarmCustomers.map((item: CustomerModel) => (
                    <CustomerRow
                      key={item.applicationId}
                      data={item}
                      agentId={selectedAgentId ?? -1}
                      columns={customersColumns}
                      isPinned={isPinned(item.applicationId.toString())}
                      isDepartmentView={isDepartmentView}
                      onAgentNameClick={handleAgentNameClick}
                      onTogglePin={handlePin}
                    />
                  ))}
                </Accordion>
              )}

              {isAgentView && (
                <Accordion
                  title="Warm"
                  id="warm"
                  count={warm.length}
                  columns={customersColumns}
                  classes={{
                    wrapper: "!bg-[#FFEDD5] !border-[#FED7AA]",
                    counter: "!bg-orange-200 !border-orange-300",
                  }}
                  expanded={expandedPanels["warm"] ?? warm.length > 0}
                  onChange={handlePanelChange("warm")}
                  sortBy={sortState["warm"]?.sortBy}
                  sortOrder={sortState["warm"]?.sortOrder}
                  onSort={(sortKey) => handleSort("warm", sortKey)}
                >
                  {warm.map((item: CustomerModel) => (
                    <CustomerRow
                      key={item.applicationId}
                      data={item}
                      agentId={selectedAgentId ?? -1}
                      columns={customersColumns}
                      isPinned={isPinned(item.applicationId.toString())}
                      isDepartmentView={isDepartmentView}
                      onAgentNameClick={handleAgentNameClick}
                      onTogglePin={handlePin}
                    />
                  ))}
                </Accordion>
              )}

              {isAgentView && (
                <Accordion
                  title="Customers"
                  id="customers"
                  count={customers.length}
                  columns={customersColumns}
                  classes={{
                    wrapper: "!bg-slate-100 !border-slate-200",
                    counter: "!bg-slate-200 !border-slate-300",
                  }}
                  expanded={expandedPanels["customers"] ?? customers.length > 0}
                  onChange={handlePanelChange("customers")}
                  sortBy={sortState["customers"]?.sortBy}
                  sortOrder={sortState["customers"]?.sortOrder}
                  onSort={(sortKey) => handleSort("customers", sortKey)}
                >
                  {customers.map((item: CustomerModel) => (
                    <CustomerRow
                      key={item.applicationId}
                      data={item}
                      agentId={selectedAgentId ?? -1}
                      columns={customersColumns}
                      isPinned={isPinned(item.applicationId.toString())}
                      isDepartmentView={isDepartmentView}
                      onAgentNameClick={handleAgentNameClick}
                      onTogglePin={handlePin}
                    />
                  ))}
                </Accordion>
              )}
            </>
          )}

          {selectedDepartmentId === "all" && (
            <div className="flex items-center justify-center py-12 text-slate-500">
              Select a team to view breakdown data
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
